/**
 * Helper type for working with DOM nodes, event coordinates etc.
 */
export type Coords = {
  x: number
  y: number
}

/**
 * Helper type for working with DOM nodes and their sizes and bounds
 */
export type RectBounds = Pick<DOMRect, 'top' | 'right' | 'bottom' | 'left'>

/**
 * Utility for events, prevents default.
 *
 * @param e DOM event or React event
 */
export function preventDefault(e: Event | React.SyntheticEvent) {
  e.preventDefault()
}

/**
 * Utility for events, prevents default AND stops propagation.
 *
 * @param e DOM event or React event
 */
export function preventDefaults(e: Event | React.SyntheticEvent) {
  e.preventDefault()
  e.stopPropagation()
}

/**
 * Typeguard for React's `MouseEvent`.
 */
export function isMouseEvent(
  e: React.BaseSyntheticEvent,
): e is React.MouseEvent {
  return e.nativeEvent instanceof MouseEvent
}

// #region ============== Drag and drop events

/**
 * Utility that checks if drag cursor has physically left the element's bounds.
 * Otherwise `onDragLeave` fires when "leaving" the element to "enter" a child
 * element.
 *
 * @note This safety check is useful even when using `pointer-events: none`
 * on child elements, as it prevents flickering when an absolute-positioned
 * drop overlay appears directly underneath the cursor.
 */
export function isDragLeavingBounds(e: DragEvent | React.DragEvent): boolean {
  if (!(e.currentTarget instanceof HTMLElement)) return false

  return !e.currentTarget.contains(e.relatedTarget as Node)
}

/**
 * Utility that checks if drag event data contains files.
 *
 * @returns {boolean} `false` for text, links, RTF, and DOM elements.
 */
export function isDraggingFiles(e: DragEvent | React.DragEvent): boolean {
  if (!e.dataTransfer) return false

  return e.dataTransfer.types && e.dataTransfer.types.includes('Files')
}

// #endregion
